import { useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Camera, Sparkles, Upload } from 'lucide-react';
import {
  useActivationParticipants,
  useCaptureUploadUrl,
  useOperatorGenerate,
} from '@/hooks/useActivationParticipants';
import { uploadFileToAzure } from '@/hooks/usePhotos';
import { useToast } from '@/hooks/use-toast';

interface OperatorCapturePanelProps {
  activationId: string;
}

/**
 * Operator Capture surface for an OperatorCurated photo booth:
 *  - lists registered guests (recent-first) and lets the operator pick one,
 *  - uploads the captured photo (capture-upload SAS → PUT → blob URL),
 *  - generates N assets (one per configured prompt, with an optional prompt override).
 */
export function OperatorCapturePanel({ activationId }: OperatorCapturePanelProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const participantsQuery = useActivationParticipants(activationId);
  const captureUpload = useCaptureUploadUrl(activationId);
  const generate = useOperatorGenerate(activationId);

  const [selectedParticipantId, setSelectedParticipantId] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [uploadedBlobUrl, setUploadedBlobUrl] = useState<string | null>(null);
  const [promptOverride, setPromptOverride] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const participants = useMemo(
    () =>
      [...(participantsQuery.data ?? [])].sort(
        (a, b) => new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime(),
      ),
    [participantsQuery.data],
  );

  const onPickFile = (f: File | null) => {
    setFile(f);
    setUploadedBlobUrl(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    try {
      const sas = await captureUpload.mutateAsync({ fileName: file.name });
      if (!sas.uploadUrl || !sas.blobUrl) {
        throw new Error('The upload URL response was incomplete.');
      }
      await uploadFileToAzure(file, sas.uploadUrl);
      setUploadedBlobUrl(sas.blobUrl);
      toast({ title: 'Photo uploaded', description: 'You can now generate the guest’s assets.' });
    } catch (e: any) {
      toast({
        title: 'Upload failed',
        description: e?.message || 'Could not upload the captured photo.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedParticipantId || !uploadedBlobUrl) return;
    const overrides = promptOverride
      .split('\n')
      .map((p) => p.trim())
      .filter(Boolean);
    await generate.mutateAsync({
      participantId: selectedParticipantId,
      data: {
        sourceBlobUrl: uploadedBlobUrl,
        prompts: overrides.length > 0 ? overrides : null,
      },
    });
    // Reset the per-capture state for the next guest.
    setPromptOverride('');
    setUploadedBlobUrl(null);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Card data-testid="operator-capture-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Camera className="h-5 w-5" />
          Operator capture
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {participantsQuery.isLoading && <Skeleton className="h-24 w-full" data-testid="participants-loading" />}
        {participantsQuery.isError && (
          <Alert variant="destructive" data-testid="participants-error">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to load registered guests</AlertTitle>
            <AlertDescription>{(participantsQuery.error as Error)?.message}</AlertDescription>
          </Alert>
        )}

        {!participantsQuery.isLoading && !participantsQuery.isError && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="operator-participant">Registered guest</Label>
              {participants.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="participants-empty">
                  No registered guests yet. Guests register by scanning the activation QR.
                </p>
              ) : (
                <Select value={selectedParticipantId} onValueChange={setSelectedParticipantId}>
                  <SelectTrigger id="operator-participant" data-testid="operator-participant-picker">
                    <SelectValue placeholder="Pick the guest you just photographed…" />
                  </SelectTrigger>
                  <SelectContent>
                    {participants.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`operator-participant-${p.id}`}>
                        {(p.generationCount ?? 0) > 0 ? 'Done — ' : ''}
                        {(p.name || 'Unnamed guest')}
                        {p.email ? ` · ${p.email}` : ''} · {new Date(p.registeredAt).toLocaleTimeString()}
                        {(p.generationCount ?? 0) > 0 ? ` · ${p.generationCount} asset(s)` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="operator-photo">Captured photo</Label>
              <Input
                id="operator-photo"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                data-testid="operator-photo-input"
                onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!file || isUploading || captureUpload.isPending}
                onClick={handleUpload}
                data-testid="operator-upload-button"
              >
                <Upload className="mr-2 h-4 w-4" />
                {isUploading ? 'Uploading…' : uploadedBlobUrl ? 'Re-upload' : 'Upload photo'}
              </Button>
              {uploadedBlobUrl && (
                <p className="text-xs text-muted-foreground" data-testid="operator-upload-ready">
                  Photo uploaded — ready to generate.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="operator-prompt-override">Prompt override (optional, one per line)</Label>
              <Textarea
                id="operator-prompt-override"
                data-testid="operator-prompt-override"
                rows={3}
                placeholder="Leave empty to use the activation’s configured prompts."
                value={promptOverride}
                onChange={(e) => setPromptOverride(e.target.value)}
              />
            </div>

            <Button
              type="button"
              disabled={!selectedParticipantId || !uploadedBlobUrl || generate.isPending}
              onClick={handleGenerate}
              data-testid="operator-generate-button"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {generate.isPending ? 'Generating…' : 'Generate assets'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
